import { config } from '../config/config';
import { supabase } from '../lib/supabase';

// Interface pour les détails de paiement PayPal
export interface PayPalElearningPaymentDetails {
  amount: number; // Montant en EUR
  currency: string; // EUR
  order_id: string;
  order_number: string;
  description?: string;
  customerName?: string;
  customerEmail?: string;
  return_base_url?: string;
}

// Interface pour la réponse d'initiation de paiement
export interface PayPalElearningPaymentResponse {
  order: {
    id: string;
    amount: number;
    status: string;
    payment_method: string;
    payment_reference: string;
    payment_url: string;
  };
  payment_reference: string;
  payment_url: string;
}

// Interface pour la réponse de statut de paiement
export interface PayPalElearningStatusResponse {
  status: 'success' | 'pending' | 'failed' | 'paid' | 'completed';
  data: any;
}

/**
 * Initie un paiement PayPal via la fonction Edge spécifique pour l'e-learning
 */
export const initiatePayPalElearningEdgePayment = async (
  details: PayPalElearningPaymentDetails
): Promise<PayPalElearningPaymentResponse> => {
  try {
    console.log('[PayPalElearningEdge] Initiation du paiement PayPal:', details);

    // Utiliser la fonction edge spécifique pour l'e-learning
    const response = await fetch(`${config.supabaseUrl}/functions/v1/elearning-paypal-initiate`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${config.supabaseKey}`,
      },
      body: JSON.stringify(details),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[PayPalElearningEdge] Erreur de la fonction edge:', errorData);
      throw new Error(errorData.error || "Échec de l'initiation du paiement PayPal");
    }

    const data = await response.json();
    console.log('[PayPalElearningEdge] Paiement PayPal initié avec succès:', data);
    return data;
  } catch (error) {
    console.error("[PayPalElearningEdge] Erreur lors de l'initiation du paiement:", error);
    throw error;
  }
};

/**
 * Vérifie le statut d'un paiement PayPal via la base de données pour l'e-learning
 */
export const checkPayPalElearningEdgePaymentStatus = async (
  reference: string,
  order_id: string
): Promise<PayPalElearningStatusResponse> => {
  const requestId = Math.random().toString(36).substring(2, 10);
  console.log(`[PayPalElearningEdge][${requestId}] Vérification du statut - reference: ${reference}, order_id: ${order_id}`);
  
  try {
    // Vérifier le statut de la commande dans la base de données
    console.log(`[PayPalElearningEdge][${requestId}] Vérification du statut dans la base de données`);
    
    const startTime = Date.now();
    const { data: order, error } = await supabase
      .from('elearn_orders')
      .select('status, payment_reference, created_at')
      .eq('id', order_id)
      .single();
    
    const queryTime = Date.now() - startTime;
    console.log(`[PayPalElearningEdge][${requestId}] Requête terminée en ${queryTime}ms`);
    console.log(`[PayPalElearningEdge][${requestId}] Résultat:`, { 
      order: order ? {
        status: order.status,
        payment_reference: order.payment_reference,
        created_at: order.created_at
      } : null, 
      error: error ? error.message : null 
    });
    
    if (error) {
      console.error(`[PayPalElearningEdge][${requestId}] Erreur lors de la récupération:`, error);
      throw new Error(`Erreur lors de la récupération de la commande: ${error.message}`);
    }
    
    if (!order) {
      console.error(`[PayPalElearningEdge][${requestId}] Commande ${order_id} introuvable`);
      throw new Error('Commande introuvable');
    }
    
    console.log(`[PayPalElearningEdge][${requestId}] Statut de la commande: ${order.status}`);
    
    // Vérifier si la commande est marquée comme payée
    if (order.status === 'completed') {
      console.log(`[PayPalElearningEdge][${requestId}] Commande déjà payée, retour immédiat`);
      return { 
        status: 'success', 
        data: { 
          order_id,
          payment_reference: reference,
          status: 'completed',
          created_at: order.created_at
        } 
      };
    }
    
    console.log(`[PayPalElearningEdge][${requestId}] Commande en attente (statut: ${order.status})`);
    
    // Retourner le statut actuel
    return {
      status: order.status === 'pending_payment' ? 'pending' : 'failed',
      data: {
        order_id,
        payment_reference: reference,
        status: order.status,
        created_at: order.created_at
      }
    };
  } catch (error) {
    console.error(`[PayPalElearningEdge][${requestId}] Erreur lors de la vérification:`, error);
    throw error;
  }
};
